import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChemicalsIcon, CostsIcon } from "../../assets/icons";
import IconButton from "../../packages/IconButton";
import ChemicalLibraryDialog from "../chemicalLibrary/ChemicalLibraryDialog";
import FeatureTourGuide from "../FeatureTourGuide";
import {
  FEATURE_SCOPE,
  FEATURE_TOUR_KEYS,
  FEATURE_TOUR_SESSION_KEYS,
} from "../../constants/global.constants";
import {
  getFeatureTourSteps,
  isFeatureTourCompleted,
} from "../FeatureTourGuide/featureTour.utils";

import "./index.scss";
import createNotification, { ERROR_NOTIFICATION } from "../Notifications";
import { completeFeatureTour } from "../../network/cipAPI";
import { getItemSession, saveItemSession } from "../../utils/session";

const PlantMenuHeader = ({
  onCostsClick,
  setShowRevisitHint,
  setForceUserProfileOpen,
  tourRestartTrigger = 0,
  currentPage = "",
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const [chemicalLibraryOpen, setChemicalLibraryOpen] = useState(false);
  const [featureTourRun, setFeatureTourRun] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setFeatureTourRun(false);
      return;
    }

    if (!isFeatureTourCompleted(FEATURE_TOUR_KEYS.CHEMICAL_UTILITY_COST)) {
      setFeatureTourRun(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!tourRestartTrigger) {
      return;
    }

    if (isLoading) {
      return;
    }

    setFeatureTourRun(true);
  }, [tourRestartTrigger, isLoading]);

  const handleFeatureTourShowMe = (isOpen = true) => {
    setForceUserProfileOpen?.(Boolean(isOpen));
  };

  const handleFeatureTourComplete = async () => {
    setFeatureTourRun(false);
    setShowRevisitHint(true);

    if (!isFeatureTourCompleted(FEATURE_TOUR_KEYS.CHEMICAL_UTILITY_COST)) {
      try {
        const payload = {
          feature_key: FEATURE_TOUR_KEYS.CHEMICAL_UTILITY_COST,
        };
        const response = await completeFeatureTour(payload);
        if (response?.status === 200) {
          const cachedValue = getItemSession(
            FEATURE_TOUR_SESSION_KEYS.COMPLETED_LIST,
          );
          const completedList = cachedValue ? JSON.parse(cachedValue) : [];
          const updatedCompletedList = [
            ...new Set([...completedList, payload.feature_key]),
          ];
          saveItemSession(
            FEATURE_TOUR_SESSION_KEYS.COMPLETED_LIST,
            JSON.stringify(updatedCompletedList),
          );
        } else {
          throw new Error("Failed to complete feature tour");
        }
      } catch (error) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      }
    }
  };

  const handleChemicalLibraryOpen = () => {
    setChemicalLibraryOpen(true);
  };

  const handleChemicalLibraryClose = () => {
    setChemicalLibraryOpen(false);
  };

  const MENU_ITEMS = [
    {
      id: "plant-chemical-library",
      key: "chemicals",
      labelKey: "plantMenu.chemicals",
      tooltipKey: "plantMenu.chemicalTooltip",
      Icon: ChemicalsIcon,
      onClick: handleChemicalLibraryOpen,
    },
    {
      id: "plant-utility-costs",
      key: "costs",
      labelKey: "plantMenu.costs",
      tooltipKey: "plantMenu.costsTooltip",
      Icon: CostsIcon,
      onClick: onCostsClick,
    },
  ];

  return (
    <div className="plant-menu-header">
      <FeatureTourGuide
        key={tourRestartTrigger}
        run={featureTourRun}
        steps={getFeatureTourSteps(
          t,
          FEATURE_TOUR_KEYS.CHEMICAL_UTILITY_COST,
          currentPage,
        )}
        showMeIndices={{ start: 2, end: 4 }}
        showMeCallback={handleFeatureTourShowMe}
        onComplete={handleFeatureTourComplete}
      />
      <div className="plant-menu-items">
        {MENU_ITEMS.map(({ id, key, labelKey, tooltipKey, Icon, onClick }) => (
          <IconButton
            key={key}
            id={id}
            className="plant-menu-item"
            onClick={onClick}
            tooltip={t(tooltipKey)}
            placement="bottom"
          >
            <div>
              <Icon />
            </div>
            <span className="plant-menu-item__label">{t(labelKey)}</span>
          </IconButton>
        ))}

        {chemicalLibraryOpen && (
          <ChemicalLibraryDialog
            scope={FEATURE_SCOPE.PLANT}
            onClose={handleChemicalLibraryClose}
          />
        )}
      </div>
    </div>
  );
};

export default PlantMenuHeader;
